<template>
  <base-modal :is-form-visible="!!item" @close="emit('close')">
    <template #header>
      <div class="flex items-center gap-2 pl-1 flex-wrap">
        <span class="text-gray-400 tabular-nums">{{ form.id }}</span>
        <span
          class="text-xs px-1.5 py-0.5 rounded"
          :class="requestKindChip(form.kind)"
        >{{ kindLabel }}</span>
        <span class="text-base font-semibold">Request</span>
        <span
          class="text-xs px-2 py-0.5 rounded"
          :class="requestStatusChip(form.status)"
        >{{ requestStatusLabel(form.status) }}</span>
        <span
          v-if="item && item.next_step_label"
          class="text-xs px-2 py-0.5 rounded"
          :class="nextStepChip(item.next_step_with)"
        >Next: {{ item.next_step_label }}</span>
      </div>
    </template>

    <template #body>
      <div class="w-full p-3 space-y-5">
        <!-- The reporter's own words. Never editable: triage answers in
             public_summary, it does not rewrite what somebody asked for. -->
        <div>
          <span class="text-xs uppercase tracking-wide text-gray-500">
            What they asked for
          </span>
          <p class="mt-1 whitespace-pre-line text-sm bg-gray-50 border rounded p-2">
            {{ item ? item.body : '' }}
          </p>
          <p class="text-xs text-gray-400 mt-1">
            {{ item ? item.reporter_display : '' }}
            <span v-if="item && item.created_at">· {{ formatDateTimeWithDay(item.created_at) }}</span>
          </p>
        </div>

        <form class="space-y-4" @submit.prevent="save">
          <div class="grid grid-cols-2 gap-4">
            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">Status</span>
              <select v-model="form.status" class="mt-1 w-full border rounded px-2 py-1.5">
                <option v-for="s in REQUEST_STATUSES" :key="s.value" :value="s.value">
                  {{ s.label }}
                </option>
              </select>
              <span v-if="statusHint" class="text-xs text-gray-500">{{ statusHint }}</span>
            </label>

            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">Type</span>
              <select v-model="form.kind" class="mt-1 w-full border rounded px-2 py-1.5">
                <option value="">— not typed yet —</option>
                <option v-for="k in REQUEST_KINDS" :key="k.value" :value="k.value">
                  {{ k.label }}
                </option>
              </select>
            </label>

            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">Department</span>
              <select
                v-model="form.department"
                class="mt-1 w-full border rounded px-2 py-1.5"
              >
                <option :value="null">— unassigned —</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">
                  {{ d.name }}
                </option>
              </select>
            </label>

            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">
                Estimated to complete
              </span>
              <select
                v-model="form.estimate"
                class="mt-1 w-full border rounded px-2 py-1.5"
              >
                <option value="">— no estimate —</option>
                <option v-for="e in REQUEST_ESTIMATES" :key="e.value" :value="e.value">
                  {{ e.label }}
                </option>
              </select>
            </label>

            <!-- Only the "specific date" band carries a date. The server
                 clears a stranded one, so this is for the person's benefit,
                 not the data's. -->
            <label v-if="needsDate" class="block col-span-2">
              <span class="text-xs uppercase tracking-wide text-gray-500">
                Which date
              </span>
              <input
                v-model="form.estimate_date"
                type="date"
                class="mt-1 w-full border rounded px-2 py-1.5"
                :class="fieldErrors.estimate_date ? 'border-red-500' : ''"
              />
              <span v-if="fieldErrors.estimate_date" class="text-xs text-red-700">
                {{ fieldErrors.estimate_date }}
              </span>
            </label>
          </div>

          <!-- Backlog item: link an existing one, or make one. -->
          <div class="border rounded p-3">
            <div class="flex items-center justify-between gap-2 flex-wrap">
              <span class="text-xs uppercase tracking-wide text-gray-500">
                Backlog item
              </span>
              <button
                v-if="!form.work_item && !creating"
                type="button"
                class="text-xs border rounded px-2 py-1 hover:bg-gray-50"
                @click="startCreating"
              >
                + New backlog item
              </button>
            </div>

            <p v-if="form.work_item" class="text-sm mt-2 flex items-center gap-2">
              <span
                class="text-xs px-1.5 py-0.5 rounded"
                :class="workItemKindClass(linkedKind)"
              >{{ linkedKind }}</span>
              <span class="tabular-nums text-gray-400">{{ form.work_item }}</span>
              <span class="min-w-0 truncate">{{ linkedTitle }}</span>
              <button
                type="button"
                class="text-xs text-gray-500 underline hover:text-red-700 ml-auto shrink-0"
                @click="form.work_item = null"
              >
                Unlink
              </button>
            </p>

            <template v-else-if="creating">
              <p class="text-xs text-gray-500 mt-2">
                Creates the item and attaches it to this request in one go.
              </p>
              <input
                ref="newTitleInput"
                v-model="newItem.title"
                type="text"
                maxlength="255"
                placeholder="What the team will actually do"
                class="mt-2 w-full border rounded px-2 py-1.5 text-sm"
              />
              <div class="grid grid-cols-2 gap-2 mt-2">
                <select v-model="newItem.kind" class="border rounded px-2 py-1.5 text-sm">
                  <option v-for="k in WORK_ITEM_KINDS" :key="k.value" :value="k.value">
                    {{ k.label }}
                  </option>
                </select>
                <select
                  v-model="newItem.department"
                  class="border rounded px-2 py-1.5 text-sm"
                >
                  <option :value="null">— pick a department —</option>
                  <option v-for="d in departments" :key="d.id" :value="d.id">
                    {{ d.name }}
                  </option>
                </select>
              </div>
              <!-- A story must hang off an epic (WorkItem.clean). Asking here
                   beats collecting a 400 from the server. -->
              <template v-if="newItemParentKinds.length">
                <select
                  v-if="!namingParent"
                  v-model="parentChoice"
                  class="mt-2 w-full border rounded px-2 py-1.5 text-sm"
                >
                  <option :value="null">
                    {{ newItemParentRequired ? '— pick a parent —' : '— no parent —' }}
                  </option>
                  <option v-for="p in newItemParentChoices" :key="p.id" :value="p.id">
                    {{ p.id }} · {{ p.kind }} · {{ p.title }}
                  </option>
                  <option v-if="canNameEpic" :value="NEW_EPIC">+ New epic…</option>
                </select>
                <!-- Nothing is created at this point. The name rides along with
                     the item in one request, so an item the server rejects
                     cannot leave an empty epic behind in the backlog. -->
                <div v-else class="mt-2 flex items-center gap-2">
                  <input
                    ref="newParentInput"
                    v-model="newItem.newParentTitle"
                    type="text"
                    maxlength="255"
                    placeholder="Name the new epic"
                    class="flex-1 border rounded px-2 py-1.5 text-sm"
                    @keyup.esc="cancelNamingParent"
                  />
                  <button
                    type="button"
                    class="border rounded px-3 py-1 text-sm hover:bg-gray-50"
                    @click="cancelNamingParent"
                  >
                    Cancel
                  </button>
                </div>
                <p v-if="namingParent" class="text-xs text-gray-500 mt-1">
                  New epic in {{ newItemDepartmentName || 'the department above' }}
                  — created when you attach.
                </p>
              </template>
              <p v-if="createError" class="text-sm text-red-700 mt-2">{{ createError }}</p>
              <div class="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  class="rounded px-3 py-1 text-sm text-white bg-accent-primary disabled:opacity-50"
                  :disabled="!canCreate || createSaving"
                  @click="createItem"
                >
                  {{ createSaving ? 'Creating…' : 'Create and attach' }}
                </button>
                <button
                  type="button"
                  class="border rounded px-3 py-1 text-sm hover:bg-gray-50"
                  @click="creating = false"
                >
                  Cancel
                </button>
              </div>
            </template>

            <template v-else>
              <input
                v-model="itemSearch"
                type="text"
                placeholder="Search the backlog to attach an existing item"
                class="mt-2 w-full border rounded px-2 py-1.5 text-sm"
              />
              <ul v-if="itemMatches.length" class="mt-1 border rounded divide-y max-h-40 overflow-auto">
                <li
                  v-for="w in itemMatches"
                  :key="w.id"
                  class="px-2 py-1 text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2"
                  @click="form.work_item = w.id"
                >
                  <span
                    class="text-xs px-1.5 py-0.5 rounded shrink-0"
                    :class="workItemKindClass(w.kind)"
                  >{{ w.kind }}</span>
                  <span class="truncate">{{ w.title }}</span>
                </li>
              </ul>
              <p
                v-else-if="itemSearch.trim()"
                class="text-xs text-gray-500 mt-1"
              >
                Nothing matches. Make one instead?
              </p>
            </template>
          </div>

          <div>
            <span class="text-xs uppercase tracking-wide text-gray-500">
              Attachments
            </span>
            <attachment-strip
              v-if="item"
              :attachments="item.attachments || []"
              target-type="request"
              :target-id="item.id"
              :can-upload="true"
              :can-remove="true"
              show-empty
            />
          </div>

          <label class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">
              What the reporter sees
            </span>
            <textarea
              v-model="form.public_summary"
              rows="2"
              class="mt-1 w-full border rounded px-2 py-1.5 font-sans"
              placeholder="Shown on their My Requests page"
            ></textarea>
          </label>

          <div class="grid grid-cols-2 gap-4">
            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">
                Date promised to the reporter
              </span>
              <input
                v-model="form.public_eta"
                type="date"
                class="mt-1 w-full border rounded px-2 py-1.5"
              />
              <span class="text-xs text-gray-400">
                Separate from the estimate above, which stays internal.
              </span>
            </label>

            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">
                Reason for declining
                <span v-if="declining" class="text-red-600">· required</span>
              </span>
              <textarea
                v-model="form.resolution_note"
                rows="2"
                class="mt-1 w-full border rounded px-2 py-1.5 font-sans"
                :class="fieldErrors.resolution_note ? 'border-red-500' : ''"
              ></textarea>
              <span v-if="fieldErrors.resolution_note" class="text-xs text-red-700">
                {{ fieldErrors.resolution_note }}
              </span>
            </label>
          </div>

          <p v-if="error" class="text-sm text-red-700">{{ error }}</p>

          <div class="flex items-center justify-end gap-2 pt-1">
            <span v-if="!dirty" class="text-xs text-gray-400 mr-auto">No changes yet</span>
            <span v-else class="text-xs text-gray-500 mr-auto">
              {{ changedFieldCount }} field{{ changedFieldCount === 1 ? '' : 's' }} changed
            </span>
            <button
              type="button"
              class="border rounded px-3 py-1.5 text-sm hover:bg-gray-50"
              :disabled="saving"
              @click="emit('close')"
            >
              Close
            </button>
            <button
              type="submit"
              class="rounded px-4 py-1.5 text-sm text-white bg-accent-primary disabled:opacity-50"
              :disabled="saving || !dirty"
            >
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>

        <!-- The thread. Posts save on their own, not with the form, so a
             half-written status change cannot swallow one. -->
        <div class="border-t pt-3">
          <span class="text-xs uppercase tracking-wide text-gray-500">
            Posts
            <span v-if="posts.length" class="text-gray-400">· {{ posts.length }}</span>
          </span>

          <p v-if="!posts.length" class="text-sm text-gray-500 mt-1">
            Nothing posted yet.
          </p>
          <div
            v-for="post in posts"
            :key="post.id"
            class="mt-2 border rounded p-2"
            :class="post.visibility === 'shared' ? 'bg-green-50 border-green-200' : 'bg-gray-50'"
          >
            <p class="whitespace-pre-line text-sm">{{ post.body }}</p>
            <p class="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
              <span>{{ post.author_display || '—' }}</span>
              <span>· {{ formatDateTimeWithDay(post.created_at) }}</span>
              <span
                class="px-1.5 py-0.5 rounded"
                :class="post.visibility === 'shared'
                  ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'"
              >{{ post.visibility === 'shared' ? 'reporter can see this' : 'internal' }}</span>
            </p>
          </div>

          <textarea
            v-model="newPost"
            rows="3"
            class="mt-3 w-full border rounded px-2 py-1.5 font-sans text-sm"
            :placeholder="postPlaceholder"
          ></textarea>
          <div class="flex items-center gap-3 mt-2 flex-wrap">
            <label class="text-sm flex items-center gap-1.5">
              <input
                v-model="newPostShared"
                type="checkbox"
                class="w-[16px] h-[16px] align-middle cursor-pointer accent-accent-primary"
              />
              Share with the reporter
            </label>
            <button
              type="button"
              class="rounded px-3 py-1 text-sm text-white bg-accent-primary disabled:opacity-50"
              :disabled="!newPost.trim() || posting"
              @click="addPost"
            >
              {{ posting ? 'Posting…' : 'Post' }}
            </button>
            <span v-if="postError" class="text-sm text-red-700">{{ postError }}</span>
          </div>
          <p v-if="needsInfoNudge" class="text-xs text-orange-700 mt-2">
            You've set this to “Need More Information” — post what you need and
            share it, or the reporter has nothing to answer.
          </p>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script setup>
import { computed, defineEmits, defineProps, nextTick, ref, watch } from 'vue'

import AttachmentStrip from '@/components/back-office/AttachmentStrip.vue'
import { useStore } from 'vuex'

import { formatDateTimeWithDay } from '@/composables/formatDateTime'
import { createPost, createWorkItemForRequest, updateRequest } from '@/services/BackOffice'
import {
  ESTIMATE_NEEDS_DATE,
  REQUEST_ESTIMATES,
  REQUEST_KINDS,
  REQUEST_STATUSES,
  nextStepChip,
  requestKindChip,
  requestStatusChip,
  requestStatusLabel,
} from '@/utils/requestOptions'
import {
  ALLOWED_PARENT_KINDS,
  KINDS_REQUIRING_PARENT,
  WORK_ITEM_KINDS,
  workItemKindClass,
} from '@/utils/workItemOptions'

// What the form owns. Everything else on the serializer is either read-only
// or derived (next_step_with, estimate_display, the post thread).
const FIELDS = [
  'kind',
  'status',
  'department',
  'work_item',
  'estimate',
  'estimate_date',
  'public_summary',
  'public_eta',
  'resolution_note',
]

const props = defineProps({
  item: { type: Object, default: null },
  departments: { type: Array, default: () => [] },
  workItems: { type: Array, default: () => [] },
})
const emit = defineEmits(['close', 'saved', 'items-added'])

const store = useStore()
const token = () => store.getters['auth/showToken']

const form = ref(blank())
const original = ref(blank())
const posts = ref([])
const saving = ref(false)
const error = ref('')
const fieldErrors = ref({})

const newPost = ref('')
const newPostShared = ref(false)
const posting = ref(false)
const postError = ref('')

const creating = ref(false)
const createSaving = ref(false)
const createError = ref('')
const newTitleInput = ref(null)
const newItem = ref(blankItem())
const itemSearch = ref('')

function blank() {
  const out = { id: null }
  for (const f of FIELDS) out[f] = null
  return out
}

function blankItem() {
  return { title: '', kind: 'task', department: null, parent: null, newParentTitle: '' }
}

function snapshot(item) {
  const out = { id: item.id }
  for (const f of FIELDS) {
    const value = item[f]
    out[f] = value === undefined ? null : value
  }
  for (const f of ['kind', 'status', 'estimate', 'public_summary', 'resolution_note']) {
    out[f] = item[f] || ''
  }
  return out
}

watch(() => props.item, item => {
  error.value = ''
  fieldErrors.value = {}
  postError.value = ''
  createError.value = ''
  newPost.value = ''
  newPostShared.value = false
  creating.value = false
  itemSearch.value = ''
  if (!item) return
  original.value = snapshot(item)
  form.value = { ...original.value }
  posts.value = [...(item.posts || [])]
  newItem.value = blankItem()
  namingParent.value = false
  newItem.value.department = item.department || null
  // A request's own words make the obvious first draft of the item's name.
  newItem.value.title = String(item.body || '').split('\n')[0].slice(0, 120)
}, { immediate: true })

const kindLabel = computed(() => {
  const found = REQUEST_KINDS.find(k => k.value === form.value.kind)
  return found ? found.label : 'untyped'
})

const needsDate = computed(() => form.value.estimate === ESTIMATE_NEEDS_DATE)
const declining = computed(() => form.value.status === 'declined')

const statusHint = computed(() => {
  if (form.value.status === 'needs_info') return 'The next step becomes the reporter\'s.'
  if (form.value.status === 'investigating') return 'Someone on the tech team is looking into it.'
  return ''
})

const needsInfoNudge = computed(() => form.value.status === 'needs_info'
  && !posts.value.some(p => p.visibility === 'shared'))

const linkedTitle = computed(() => {
  if (props.item && props.item.work_item === form.value.work_item) {
    return props.item.work_item_title || `item ${form.value.work_item}`
  }
  const found = props.workItems.find(w => w.id === form.value.work_item)
  return found ? found.title : `item ${form.value.work_item}`
})

const linkedKind = computed(() => {
  if (props.item && props.item.work_item === form.value.work_item) {
    return props.item.work_item_kind || 'item'
  }
  const found = props.workItems.find(w => w.id === form.value.work_item)
  return found ? found.kind : 'item'
})

const itemMatches = computed(() => {
  const needle = itemSearch.value.trim().toLowerCase()
  if (!needle) return []
  return props.workItems
    .filter(w => String(w.title || '').toLowerCase().includes(needle))
    .slice(0, 25)
})

// A sentinel option rather than a second modal: stacking one on top of this
// one means two focus traps arguing over Escape, and this form holds unsaved
// edits that a re-render would discard.
const NEW_EPIC = '__new_epic__'
const namingParent = ref(false)
const newParentInput = ref(null)

const newItemParentKinds = computed(() => ALLOWED_PARENT_KINDS[newItem.value.kind] || [])
const newItemParentRequired = computed(
  () => KINDS_REQUIRING_PARENT.includes(newItem.value.kind),
)
const newItemParentChoices = computed(() => props.workItems
  .filter(w => newItemParentKinds.value.includes(w.kind))
  .slice(0, 200))

const canNameEpic = computed(() => newItemParentKinds.value.includes('epic'))

const newItemDepartmentName = computed(() => {
  const match = props.departments.find(d => d.id === newItem.value.department)
  return match ? match.name : ''
})

// Picking the sentinel is a mode switch, not a value, so it never reaches
// newItem.parent -- which would otherwise be sent to the server as an id.
const parentChoice = computed({
  get: () => newItem.value.parent,
  set(value) {
    if (value === NEW_EPIC) {
      startNamingParent()
      return
    }
    newItem.value.parent = value
  },
})

const canCreate = computed(() => Boolean(
  newItem.value.title.trim()
  && newItem.value.department
  && (!newItemParentRequired.value
      || newItem.value.parent
      || newItem.value.newParentTitle.trim()),
))

async function startNamingParent() {
  newItem.value.parent = null
  namingParent.value = true
  await nextTick()
  if (newParentInput.value) newParentInput.value.focus()
}

function cancelNamingParent() {
  namingParent.value = false
  newItem.value.newParentTitle = ''
}

// A kind change can strand a parent the new kind may not have.
watch(() => newItem.value.kind, kind => {
  const allowed = ALLOWED_PARENT_KINDS[kind] || []
  if (!allowed.includes('epic')) cancelNamingParent()
  if (!allowed.length) {
    newItem.value.parent = null
    return
  }
  const current = props.workItems.find(w => w.id === newItem.value.parent)
  if (current && !allowed.includes(current.kind)) newItem.value.parent = null
})

const changed = computed(() => {
  const out = {}
  for (const f of FIELDS) {
    if (form.value[f] !== original.value[f]) out[f] = form.value[f]
  }
  // The server clears the date itself when the band is not a specific date,
  // but sending the pair together keeps the payload honest about intent.
  if ('estimate' in out && !needsDate.value) out.estimate_date = null
  return out
})
const changedFieldCount = computed(() => Object.keys(changed.value).length)
const dirty = computed(() => changedFieldCount.value > 0)

function validate() {
  const errors = {}
  if (needsDate.value && !form.value.estimate_date) {
    errors.estimate_date = 'Pick the date, or choose one of the ranges instead.'
  }
  if (declining.value
      && !String(form.value.resolution_note || '').trim()
      && !String(form.value.public_summary || '').trim()) {
    errors.resolution_note = 'Declining needs a reason the reporter can read.'
  }
  fieldErrors.value = errors
  return !Object.keys(errors).length
}

async function save() {
  error.value = ''
  if (!validate() || !dirty.value) return

  saving.value = true
  try {
    const updated = await updateRequest(token(), form.value.id, { ...changed.value })
    emit('saved', updated)
    emit('close')
  } catch (e) {
    applyServerErrors(e)
  } finally {
    saving.value = false
  }
}

async function addPost() {
  postError.value = ''
  posting.value = true
  try {
    const created = await createPost(token(), {
      target_type: 'Request',
      target_id: form.value.id,
      body: newPost.value.trim(),
      visibility: newPostShared.value ? 'shared' : 'internal',
    })
    posts.value.push(created)
    newPost.value = ''
    newPostShared.value = false
    // The row behind the modal carries its own copy of the thread. Only the
    // thread is sent: the rest of the form may be mid-edit.
    emit('saved', { id: form.value.id, posts: [...posts.value] })
  } catch (e) {
    postError.value = describe(e)
  } finally {
    posting.value = false
  }
}

async function startCreating() {
  creating.value = true
  await nextTick()
  if (newTitleInput.value) newTitleInput.value.focus()
}

async function createItem() {
  createError.value = ''
  createSaving.value = true
  try {
    const payload = {
      title: newItem.value.title.trim(),
      kind: newItem.value.kind,
      department: newItem.value.department,
    }
    // One or the other, never both -- the server refuses the pair, and this
    // keeps that refusal a backstop rather than something you can trip over.
    const epicName = newItem.value.newParentTitle.trim()
    if (namingParent.value && epicName) payload.new_parent_title = epicName
    else if (newItem.value.parent) payload.parent = newItem.value.parent
    const result = await createWorkItemForRequest(token(), form.value.id, payload)
    // The server links the two and hands back the request as it now stands,
    // so the form has to follow rather than keep its own idea of it.
    original.value = snapshot(result.request)
    form.value = { ...original.value, ...changed.value, work_item: result.request.work_item }
    creating.value = false
    cancelNamingParent()
    // The epic exists now but the triage page's list was fetched before it
    // did, so hand it up rather than make the next request refetch to find it.
    const added = [result.parent, result.work_item].filter(Boolean)
    if (added.length) emit('items-added', added)
    emit('saved', result.request)
  } catch (e) {
    createError.value = describe(e)
  } finally {
    createSaving.value = false
  }
}

function applyServerErrors(e) {
  const data = e?.response?.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const errors = {}
    for (const [field, messages] of Object.entries(data)) {
      const text = Array.isArray(messages) ? messages.join(' ') : String(messages)
      if (FIELDS.includes(field)) errors[field] = text
      else error.value = `${error.value} ${field}: ${text}`.trim()
    }
    fieldErrors.value = errors
    if (!error.value && !Object.keys(errors).length) error.value = describe(e)
  } else {
    error.value = describe(e)
  }
}

function describe(e) {
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

const postPlaceholder = 'A note on this request. Internal unless you share it.'
</script>
